/* eslint-disable max-lines */
import {
  CustomVideoControls,
  useMatchedFaceLayers,
} from '@hakimo-ui/hakimo/feature-shared';
import { Box, RgMatchedFace } from '@hakimo-ui/hakimo/types';
import { FaceProfileOverlay } from '@hakimo-ui/hakimo/ui-elements';
import { onTimestampClick, useFullscreen } from '@hakimo-ui/hakimo/util';
import { Button, useRerenderOnWindowResize } from '@hakimo-ui/shared/ui-base';
import { BackwardIcon, ForwardIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import AlarmVideoBox from './AlarmVideoBox';

export interface AlarmVideoHandle {
  seek: (timeSec: number) => void;
}

export interface Props {
  videoPath?: string;
  startTime?: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: (isAutoplay?: boolean) => void;
  onPrevious: () => void;
  boxes?: Box[];
  isAutoplayEnabled: boolean;
  matchedFaces?: RgMatchedFace[];
  cameraName?: string;
  videoStartTime?: string;
  disableDownload?: boolean;
  alarmType?: string;
}

export const AlarmVideo = forwardRef<AlarmVideoHandle, Props>((props, ref) => {
  const {
    videoPath,
    startTime = 0,
    hasNext,
    hasPrevious,
    onNext,
    onPrevious,
    boxes,
    isAutoplayEnabled,
    matchedFaces,
    cameraName,
    videoStartTime,
    disableDownload,
    alarmType,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playRef = useRef<Promise<void> | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const { isFullScreen, toggleFullScreen } = useFullscreen(containerRef);

  useRerenderOnWindowResize();

  const { overlayFaces, boxes: combinedBoxes } = useMatchedFaceLayers({
    matchedFaces,
    cameraName,
    videoStartTime,
    alarmType,
    baseBoxes: boxes,
  });

  useImperativeHandle(
    ref,
    () => ({
      seek: (timeSec: number) => {
        const video = videoRef.current;
        if (video) {
          onTimestampClick(video, timeSec);
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const video = videoRef.current;
    const onLoadedMetadata = () => {
      setIsVideoLoaded(true);
      playRef.current = video
        .play()
        .catch((error) => {
          Sentry.captureMessage('Failed to play video', {
            extra: { videoPath, error },
          });
        })
        .finally(() => {
          playRef.current = null;
        });
    };

    if (videoPath) {
      video.load();
      video.currentTime = startTime;
      video.addEventListener('loadedmetadata', onLoadedMetadata);
    }

    return () => {
      setIsVideoLoaded(false);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [videoPath, startTime]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    const video = videoRef.current;
    const onVideoEnd = () => {
      isAutoplayEnabled && onNext(true);
    };
    const onPause = (e: Event) => {
      if (playRef.current) {
        e.preventDefault();
      }
    };
    if (videoPath) {
      video.addEventListener('ended', onVideoEnd);
      video.addEventListener('pause', onPause);
    }
    return () => {
      video.removeEventListener('ended', onVideoEnd);
      video.removeEventListener('pause', onPause);
    };
  }, [isAutoplayEnabled, onNext, videoPath]);

  return (
    <div className="relative mb-1 space-y-3">
      <div className="group relative overflow-hidden" ref={containerRef}>
        <video
          ref={videoRef}
          className="dark:bg-ondark-bg-2 bg-onlight-bg-2 peer aspect-video w-full object-contain"
          muted
          onContextMenu={
            disableDownload ? (e) => e.preventDefault() : undefined
          }
        >
          <source src={videoPath} type="video/mp4"></source>
        </video>
        <CustomVideoControls
          isFullScreen={isFullScreen}
          onFullScreen={toggleFullScreen}
          videoRef={videoRef}
          containerRef={containerRef}
        />
        {hasPrevious && (
          <div className="invisible absolute left-2 top-1/2 -translate-y-1/2 group-hover:visible">
            <Button variant="icon" onClick={onPrevious}>
              <BackwardIcon className="w-5" />
            </Button>
          </div>
        )}
        {hasNext && (
          <div className="invisible absolute right-2 top-1/2 -translate-y-1/2 group-hover:visible">
            <Button variant="icon" onClick={() => onNext()}>
              <ForwardIcon className="w-5" />
            </Button>
          </div>
        )}
        {overlayFaces.length > 0 && (
          <FaceProfileOverlay matchedFaces={overlayFaces} />
        )}
        {isVideoLoaded && (
          <AlarmVideoBox
            boxes={combinedBoxes}
            videoRef={videoRef}
            videoHeight={videoRef.current?.videoHeight}
            videoWidth={videoRef.current?.videoWidth}
            videoRect={videoRef.current?.getBoundingClientRect()}
          />
        )}
      </div>
    </div>
  );
});

export default AlarmVideo;
